import { formatMoney, holdsMemberships } from '@app/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { currentProduct } from '../lib/public-router'
import { useTRPC } from '../lib/trpc'
import { Button, Choice, Notice, Toggle } from '../ui'
import { Select } from '../ui/Select'

const money = formatMoney

/**
 * Putting a standing arrangement on the card, from the same link that pays a
 * bill.
 *
 * **Monthly is auto-pay; yearly chooses.** Twelve manual payments a year is the
 * workflow this exists to remove, so offering "monthly, and I'll pay each time"
 * beside it would hand back the problem — somebody who would rather pay monthly
 * by check arranges that with the office, where every non-card path already
 * lives. A year is long enough a commitment that both answers are ordinary,
 * which is what earns the second control there and not here.
 *
 * **The words come from the server and go back unchanged.** What is stored has
 * to be what was on the screen, so this displays `consentText` and echoes the
 * same string; the server recomposes it and refuses a mismatch. Nothing here
 * writes a sentence of its own.
 */
export function PayAutopay({
  token,
  justPaid,
  leaving = null,
}: {
  token: string
  /** They came back from Checkout a moment ago, so the card may not be in yet. */
  justPaid?: boolean
  /** An arrangement another section of the page manages — the membership. */
  leaving?: string | null
}) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const key = trpc.pay.autopay.queryKey({ token })
  const offer = useQuery({
    ...trpc.pay.autopay.queryOptions({ token }),
    retry: false,
  })

  const accept = useMutation({
    ...trpc.pay.autopayAccept.mutationOptions(),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  })
  const stop = useMutation({
    ...trpc.pay.autopayStop.mutationOptions(),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  })

  const arrangements =
    offer.data?.arrangements.filter((a) => a.id !== leaving) ?? []
  if (
    !offer.data ||
    (arrangements.length === 0 && offer.data.plans.length === 0)
  )
    return null
  const { plans, people, hasCard, acceptsPayments } = offer.data
  if (!acceptsPayments) return null

  return (
    <section className="pay__autopay">
      <h2 className="pay__h2">Paying automatically</h2>
      {arrangements.map((a) => (
        <Arrangement
          key={a.id}
          arrangement={a}
          people={people}
          hasCard={hasCard}
          justPaid={justPaid}
          busy={accept.isPending || stop.isPending}
          onAccept={(personId, cadence, consentText) =>
            accept.mutate({
              token,
              arrangementId: a.id,
              personId,
              cadence,
              consentText,
            })
          }
          onStop={(mandateId) => stop.mutate({ token, mandateId })}
        />
      ))}
      {plans.map((p) => (
        <Arrangement
          key={p.id}
          arrangement={p}
          people={people}
          hasCard={hasCard}
          justPaid={justPaid}
          busy={accept.isPending || stop.isPending}
          onAccept={(personId, cadence, consentText) =>
            accept.mutate({
              token,
              paymentPlanId: p.id,
              personId,
              cadence,
              consentText,
            })
          }
          onStop={(mandateId) => stop.mutate({ token, mandateId })}
        />
      ))}
      {accept.error && <Notice tone="error">{accept.error.message}</Notice>}
      {stop.error && <Notice tone="error">{stop.error.message}</Notice>}
    </section>
  )
}

function Arrangement({
  arrangement,
  people,
  hasCard,
  justPaid,
  busy,
  onAccept,
  onStop,
}: {
  arrangement: {
    id: string
    label: string
    cadence: 'monthly' | 'annual'
    mandateId: string | null
    mandateText: string | null
    options: {
      cadence: 'monthly' | 'annual'
      rateCents: number
      consentText: string
    }[]
  }
  people: { id: string; name: string }[]
  hasCard: boolean
  justPaid?: boolean
  busy: boolean
  onAccept: (
    personId: string,
    cadence: 'monthly' | 'annual',
    consentText: string,
  ) => void
  onStop: (mandateId: string) => void
}) {
  const monthly = arrangement.options.find((o) => o.cadence === 'monthly')
  const annual = arrangement.options.find((o) => o.cadence === 'annual')
  const [cadence, setCadence] = useState<'monthly' | 'annual'>(
    arrangement.cadence,
  )
  /* Preselected, because renewing is what somebody choosing a yearly membership
     usually means — and it is a tick they can see and undo, not a default that
     acts on its own. Unticked, yearly is one payment covering this year. */
  const [renew, setRenew] = useState(true)
  const [personId, setPersonId] = useState(people[0]?.id ?? '')

  /* Already agreed: the words are read back rather than retold, so what is on
     the screen is what the shul is holding. */
  if (arrangement.mandateId) {
    return (
      <div className="pay__auto">
        <p className="pay__auto-head">{arrangement.label}</p>
        <p className="pay__auto-text">{arrangement.mandateText}</p>
        <Button
          variant="secondary"
          disabled={busy}
          onClick={() => onStop(arrangement.mandateId as string)}
        >
          Stop automatic payments
        </Button>
        <p className="pay__fine">
          {holdsMemberships(currentProduct())
            ? 'Stopping this changes nothing about the membership itself — you stay a member, and we will send you a bill as usual.'
            : 'Stopping this ends only the automatic charge — we will send you a bill as usual.'}
        </p>
      </div>
    )
  }

  const chosen = cadence === 'monthly' ? monthly : annual
  /* Yearly can be a single payment for this year, which is a mandate nobody is
     agreeing to — so there is nothing to accept and the ordinary Pay button
     above is what does it. */
  const agreeing = cadence === 'monthly' || renew

  return (
    <div className="pay__auto">
      <p className="pay__auto-head">{arrangement.label}</p>
      <Choice
        label="How would you like to pay?"
        value={cadence}
        onValueChange={setCadence}
        options={[
          {
            value: 'monthly' as const,
            label: monthly ? `${money(monthly.rateCents)} a month` : 'Monthly',
            description: 'Charged to your card automatically each month.',
          },
          {
            value: 'annual' as const,
            label: annual ? `${money(annual.rateCents)} a year` : 'Yearly',
            description: 'One payment covering twelve months.',
          },
        ]}
      />

      {cadence === 'annual' && (
        <div className="pay__renew">
          <Toggle
            checked={renew}
            onChange={setRenew}
            label="Renew automatically each year"
          />
          <p className="pay__fine">
            We will email you before each year's charge. Turned off, this is one
            payment covering the coming year.
          </p>
        </div>
      )}

      {agreeing && chosen && (
        <>
          {!hasCard && (
            <Notice>
              {justPaid
                ? /* The payment is written by the webhook, which lands a moment
                     after the browser comes back — so "no card" here means we
                     cannot yet name the card they just paid with. */
                  'We are still recording the payment you just made. Give it a moment and reload, and agreeing here will charge the card you just paid with.'
                : 'Make a payment above first. Agreeing here charges the card you pay with automatically.'}
            </Notice>
          )}
          {people.length > 1 && (
            <label className="pay__field" htmlFor={`who-${arrangement.id}`}>
              <span className="pay__label">Who is agreeing to this?</span>
              <Select
                id={`who-${arrangement.id}`}
                value={personId}
                onValueChange={setPersonId}
                options={people.map((p) => ({ value: p.id, label: p.name }))}
              />
            </label>
          )}
          {/* The exact sentence that will be stored, shown before agreeing to
              it — not a summary of it. */}
          <p className="pay__auto-text">{chosen.consentText}</p>
          <Button
            disabled={busy || !hasCard || !personId}
            onClick={() =>
              onAccept(personId, chosen.cadence, chosen.consentText)
            }
          >
            {cadence === 'monthly'
              ? `Pay ${money(chosen.rateCents)} a month automatically`
              : `Renew ${money(chosen.rateCents)} a year automatically`}
          </Button>
        </>
      )}

      {cadence === 'annual' && !renew && (
        <p className="pay__fine">
          Nothing to set up — pay the year above and we will ask you again next
          year.
        </p>
      )}
    </div>
  )
}
